// Baghdad wall-clock helpers for the booking panel: day chips, slot groups,
// the summary line and the «تم الحجز.» toast. Display only — no figure here
// is a sum (rule 7); an end time is the slot's own `end` from the API, or
// start + the service's API duration for «الآن».

import { AR_WEEKDAYS, baghdadParts, baghdadToday, formatDate, formatTime } from '@/lib/format';
import { toArabicDigits } from '@/lib/digits';

/** YYYY-MM-DD + n days (plain calendar arithmetic, no timezone). */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Baghdad calendar date of an instant. */
export function baghdadDateOf(iso: string | Date): string {
  const p = baghdadParts(iso);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Weekday name of a plain date. */
export function weekdayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return AR_WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

/** «الأربعاء ١٦» — weekday + day (the selected «غداً» chip suffix). */
export function weekdayDay(date: string): string {
  return `${weekdayOf(date)} ${toArabicDigits(Number(date.slice(8, 10)))}`;
}

export type DayKind = 'today' | 'tomorrow' | 'other';

export function dayKind(date: string, now: Date = new Date()): DayKind {
  const today = baghdadToday(now);
  if (date === today) return 'today';
  if (date === addDays(today, 1)) return 'tomorrow';
  return 'other';
}

/** Morning <12:00 · afternoon 12:00–14:59 · evening ≥15:00 (boards: ٣:٠٠ م sits under «مساءً»). */
export type SlotGroup = 'morning' | 'afternoon' | 'evening';

export const SLOT_GROUP_LABEL: Record<SlotGroup, string> = {
  morning: 'صباحاً',
  afternoon: 'بعد الظهر',
  evening: 'مساءً',
};

export function slotGroupOf(iso: string): SlotGroup {
  const h = baghdadParts(iso).hour;
  if (h < 12) return 'morning';
  if (h < 15) return 'afternoon';
  return 'evening';
}

/** «١٠:٣٠ – ١١:١٥ ص» (one suffix when both ends share it), else «١١:٣٠ ص – ١٢:١٥ م». */
export function timeRange(startIso: string, endIso: string): string {
  const sameHalf = baghdadParts(startIso).hour < 12 === baghdadParts(endIso).hour < 12;
  if (sameHalf) return `${formatTime(startIso, { suffix: false })} – ${formatTime(endIso)}`;
  return `${formatTime(startIso)} – ${formatTime(endIso)}`;
}

/**
 * The day part of the footer summary, as the boards write it:
 *   today    → «اليوم ٤:٠٠ – ٥:٣٠ م»              (joined by a space)
 *   «الآن»   → «الآن ٤:٤٥ – ٥:٠٠ م»
 *   tomorrow → «غداً الأربعاء ١٦ أيلول · ١٠:٣٠ – ١١:١٥ ص»
 *   other    → «الخميس ١٧ أيلول · …»
 */
export function summaryWhen(startIso: string, endIso: string, isNow: boolean, now: Date = new Date()): string {
  const range = timeRange(startIso, endIso);
  if (isNow) return `الآن ${range}`;
  const date = baghdadDateOf(startIso);
  const kind = dayKind(date, now);
  if (kind === 'today') return `اليوم ${range}`;
  const full = formatDate(date, { weekday: true });
  return kind === 'tomorrow' ? `غداً ${full} · ${range}` : `${full} · ${range}`;
}

/** «غداً ١٠:٣٠ ص» / «اليوم ٤:٠٠ م» / «الخميس ١٧ أيلول ١٠:٣٠ ص» — the created toast. */
export function shortWhen(startIso: string, now: Date = new Date()): string {
  const date = baghdadDateOf(startIso);
  const kind = dayKind(date, now);
  const t = formatTime(startIso);
  if (kind === 'today') return `اليوم ${t}`;
  if (kind === 'tomorrow') return `غداً ${t}`;
  return `${formatDate(date, { weekday: true })} ${t}`;
}

/** The current minute as an RFC 3339 instant (the «الآن» start). */
export function nowMinuteIso(now: Date = new Date()): string {
  const d = new Date(now.getTime());
  d.setUTCSeconds(0, 0);
  return d.toISOString().replace('.000Z', 'Z');
}

/** start + minutes (for «الآن»: the service's API duration). */
export function plusMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString().replace('.000Z', 'Z');
}

/** «HH:MM» Baghdad wall-clock of an instant (matches a `?time=` prefill). */
export function wallClock(iso: string | Date): string {
  const p = baghdadParts(iso);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}
